import React, { useState, useEffect } from "react";
import {
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  LinearProgress,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle,
  Schedule,
  Error,
  Send,
} from "@mui/icons-material";
import Moment from "moment";
import { useNavigate } from "react-router-dom";
import { DistributionsApi } from "../../services/api/distributions";

Moment.locale("en");

function DistributionItem(props) {
  const { item, token } = props;
  const navigate = useNavigate();
  const [progressData, setProgressData] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isSyncingStatus, setIsSyncingStatus] = useState(false);
  const [currentDistributionStatus, setCurrentDistributionStatus] = useState(item.distributionStatus);
  
  // Add defensive checks for undefined/null item
  if (!item) {
    console.warn('DistributionItem received undefined item');
    return null;
  }
  
  // Handle the new backend data format - item is now a flat object
  const redirect = `/distributions/${item.relId || item.id}`;
  const subject = item.subject || 'No Subject';
  const createdAt = item.createdAt;
  
  // Get auth token helper
  const getAuthToken = async () => {
    let authToken = token;
    if (typeof authToken === 'function') {
      authToken = await authToken();
    }
    return authToken;
  };

  // Fetch progress data for in-progress distributions
  const fetchProgress = async () => {
    try {
      const authToken = await getAuthToken();
      if (!authToken) return;
      
      console.log('Fetching progress for distribution:', item.relId || item.id);
      const response = await DistributionsApi.getDistributionProgress(authToken, item.relId || item.id);
      console.log('Raw API response:', response);
      console.log('Response keys:', Object.keys(response || {}));
      console.log('Response.data:', response?.data);
      
      // Handle different response structures
      let progressData = null;
      if (response && response.success && response.data) {
        progressData = response.data;
      } else if (response && response.data) {
        // Sometimes the data is still in response.data even if success is false
        progressData = response.data;
      } else if (response && typeof response === 'object' && !response.success && !response.data) {
        // Sometimes the response itself is the data
        progressData = response;
      }
      
      if (progressData) {
        console.log('Progress data received:', {
          allEmailsComplete: progressData.allEmailsComplete,
          distributionStatus: progressData.distributionStatus,
          completed: progressData.completed,
          sent: progressData.sent,
          total: progressData.total,
          failed: progressData.failed,
          processing: progressData.processing,
          queued: progressData.queued
        });
        
        setProgressData(progressData);
        
        // If all emails are complete but distribution status is still PROCESSING, update it
        console.log('Checking update conditions:', {
          allEmailsComplete: progressData.allEmailsComplete,
          distributionStatus: progressData.distributionStatus,
          shouldUpdate: progressData.allEmailsComplete && progressData.distributionStatus === 'PROCESSING'
        });
        
        if (progressData.allEmailsComplete && progressData.distributionStatus === 'PROCESSING') {
          console.log('Triggering status update to:', progressData.failed > 0 ? 'FAILED' : 'SUCCESS');
          await updateDistributionStatus(authToken, progressData);
        }
      } else {
        console.log('No response data or response is falsy:', response);
      }
    } catch (error) {
      console.error('Failed to fetch distribution progress:', error);
    }
  };

  // Update distribution status when all emails are complete
  const updateDistributionStatus = async (authToken, progressData) => {
    setIsSyncingStatus(true);
    try {
      const finalStatus = progressData.failed > 0 ? 'FAILED' : 'SUCCESS';
      console.log('Updating distribution status from PROCESSING to:', finalStatus);
      
      const updateData = {
        relId: item.relId || item.id,
        type: 'updateStatus',
        data: {
          distributionStatus: finalStatus
        }
      };
      
      console.log('Sending update request with data:', updateData);
      
      const updateResponse = await DistributionsApi.updateDistribution(authToken, updateData);
      console.log('Update response:', updateResponse);
      
      if (updateResponse.success) {
        console.log(`Distribution status updated to ${finalStatus}`);
        // Force a refresh of the parent distributions list and wait for backend truth
        if (typeof props.onDistributionUpdate === 'function') {
          await props.onDistributionUpdate(true);
        }
      } else {
        console.error('Failed to update distribution status:', updateResponse);
      }
    } catch (error) {
      console.error('Failed to update distribution status:', error);
    } finally {
      setIsSyncingStatus(false);
    }
  };

  // Set up polling for in-progress distributions
  useEffect(() => {
    // Stop polling if distribution status has been updated to final state
    const isFinalStatus = currentDistributionStatus === 'SUCCESS' || 
                         currentDistributionStatus === 'FAILED' ||
                         currentDistributionStatus === 'CANCELLED';
    
    const shouldPoll = currentDistributionStatus === 'PROCESSING' && !isFinalStatus;
    
    console.log('Polling decision:', {
      currentDistributionStatus,
      shouldPoll,
      isPolling,
      isFinalStatus
    });
    
    if (shouldPoll && !isPolling) {
      console.log('Starting polling for distribution:', item.relId || item.id);
      setIsPolling(true);
      fetchProgress(); // Initial fetch
      
      const interval = setInterval(fetchProgress, 10000); // Poll every 10 seconds
      
      return () => {
        console.log('Stopping polling for distribution:', item.relId || item.id);
        clearInterval(interval);
        setIsPolling(false);
      };
    } else if (!shouldPoll && isPolling) {
      console.log('Should stop polling for distribution:', item.relId || item.id);
      setIsPolling(false);
    }
  }, [currentDistributionStatus, item.relId, item.id]);

  // Sync local status with item changes
  useEffect(() => {
    setCurrentDistributionStatus(item.distributionStatus);
  }, [item.distributionStatus]);
  
  // Determine progress based on distributionStatus and real-time data
  const getProgress = () => {
    // Use real-time progress data if available
    if (progressData) {
      const completed = progressData.sent || progressData.completed || 0;
      const total = progressData.total || 0;
      const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
      
      // If all emails are complete, determine final status
      if (progressData.allEmailsComplete) {
        const statusToCheck = progressData.distributionStatus || currentDistributionStatus || item.distributionStatus;
        if (statusToCheck === 'PROCESSING') {
          return {
            percent: 100,
            completed,
            total,
            failed: progressData.failed || 0,
            processing: progressData.processing || 0,
            queued: progressData.queued || 0,
            finalizing: true,
            isRealTime: true,
            icon: <Send color="info" />
          };
        }

        const hasFailed = progressData.failed > 0;
        return {
          done: true,
          failed: hasFailed,
          icon: hasFailed ? <Error color="error" /> : <CheckCircle color="success" />,
          completed: completed,
          total: total
        };
      }
      
      // Still processing
      return { 
        percent, 
        completed: completed, 
        total: total,
        failed: progressData.failed || 0,
        processing: progressData.processing || 0,
        queued: progressData.queued || 0,
        isRealTime: true 
      };
    }
    
    // Fallback to item status if no progress data
    const statusToCheck = currentDistributionStatus || item.distributionStatus;
    switch (statusToCheck) {
      case 'SUCCESS':
        return { done: true, icon: <CheckCircle color="success" /> };
      case 'FAILED':
        return { done: true, failed: true, icon: <Error color="error" /> };
      case 'SCHEDULED':
        const scheduledDate = Moment.unix(item.startTime).format("MMM DD, YYYY");
        return { scheduled: true, schedule: scheduledDate, icon: <Schedule color="warning" /> };
      case 'PROCESSING':
        return { percent: 0, icon: <Send color="info" /> };
      default:
        return { percent: 0 };
    }
  };
  
  const progress = getProgress();

  const formattedDate = createdAt 
    ? (Moment.unix(createdAt).format("LL") === "Invalid date"
        ? new Date(createdAt * 1000).toLocaleDateString()
        : Moment.unix(createdAt).format("LL"))
    : 'Unknown Date';

  const renderProgress = () => {
    if (progress.done) {
      if (progress.failed) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {progress.icon}
            <Chip label="Distribution Failed" color="error" size="small" />
          </Box>
        );
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {progress.icon}
          <Chip label="Distribution Complete" color="success" size="small" />
        </Box>
      );
    }
    if (progress.scheduled) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {progress.icon}
          <Chip
            label={`Scheduled for ${progress.schedule}`}
            color="warning"
            size="small"
          />
        </Box>
      );
    }
    
    // Show progress bar for in-progress distributions
    return (
      <Box sx={{ width: "100%", mr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
          {progress.icon}
          {(isPolling || isSyncingStatus) && <CircularProgress size={16} />}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {progress.finalizing ? 'Finalizing Distribution' : progress.isRealTime ? 'Processing Distribution' : 'Processing...'}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={progress.percent || 0} 
          sx={{ height: 6, borderRadius: 3 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {progress.isRealTime ? (
            <>
              {progress.completed || 0} of {progress.total || 0} emails sent
              {progress.failed > 0 && `, ${progress.failed} failed`}
              {progress.processing > 0 && `, ${progress.processing} processing`}
              {progress.queued > 0 && `, ${progress.queued} queued`}
            </>
          ) : (
            `${Math.round(progress.percent || 0)}% complete`
          )}
        </Typography>
      </Box>
    );
  };

  return (
    <ListItem disablePadding>
      <ListItemButton 
        onClick={() => navigate(redirect)}
        sx={{ 
          py: 2, 
          px: 3,
          '&:hover': {
            backgroundColor: 'action.hover',
          }
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, flexGrow: 1, mr: 2 }}>
              {subject}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
              {formattedDate}
            </Typography>
          </Box>
          <Box sx={{ mt: 1.5 }}>{renderProgress()}</Box>
        </Box>
      </ListItemButton>
    </ListItem>
  );
}

export default DistributionItem;
